import { ProductFlowDiagram, type FlowStep } from "@/components/products/ProductFlowDiagram"
import { FAQAccordion, type FAQItem } from "@/components/public/FAQAccordion"
import { LandingFooter } from "@/components/public/LandingFooter"
import { PublicHeader } from "@/components/public/PublicHeader"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { BreathingGradient } from "@/components/ui/living-brand"
import { SpotlightCard } from "@/components/ui/SpotlightCard"
import { Heading } from "@/components/ui/typography"
import { cn } from "@/lib/utils"
import { semanticTokens } from "@supabank/config/theme"
import { Page } from "@supabank/ui"
import { ArrowRight, CheckCircle2, CreditCard, Globe2, PlusCircle, Shield } from "lucide-react"
import { useEffect } from "react"
import { Link } from "react-router-dom"

const flowSteps: FlowStep[] = [
  {
    id: "create",
    title: "Create Your Address",
    description: "Get a permanent crypto address in one click. Works forever—no expiration.",
    icon: <PlusCircle className="h-6 w-6" />,
  },
  {
    id: "send",
    title: "Send Crypto",
    description: "Send USDC, USDT, or other supported assets to your address on any supported network.",
    icon: <CreditCard className="h-6 w-6" />,
  },
  {
    id: "convert",
    title: "Automatic Conversion",
    description: "We detect the deposit and automatically convert it to EUR or USD at market rates.",
    icon: <Globe2 className="h-6 w-6" />,
  },
  {
    id: "settle",
    title: "Bank Deposit",
    description: "Funds arrive in your linked bank account via SEPA, ACH, or Wire—typically within 1-2 business days.",
    icon: <CheckCircle2 className="h-6 w-6" />,
  },
]

const supportedAssets = [
  { name: "USDC", networks: 8, description: "Available on Ethereum, Solana, Polygon, Arbitrum, Optimism, Base, Avalanche, Stellar" },
  { name: "USDT", networks: 2, description: "Available on Ethereum and Tron" },
  { name: "PYUSD", networks: 2, description: "Available on Ethereum and Solana" },
  { name: "USDB", networks: 2, description: "Available on Base and Solana" },
  { name: "EURC", networks: 2, description: "Available on Base and Solana" },
  { name: "DAI", networks: 1, description: "Available on Ethereum" },
  { name: "USDP", networks: 1, description: "Available on Solana" },
  { name: "ETH", networks: 1, description: "Available on Ethereum" },
]

const supportedChains = [
  "Ethereum",
  "Solana",
  "Polygon",
  "Arbitrum",
  "Optimism",
  "Base",
  "Avalanche",
  "Stellar",
  "Tron",
]

const useCases = [
  {
    title: "Receive Crypto Payments",
    description: "Give customers a permanent address. Every payment automatically converts to fiat in your bank.",
  },
  {
    title: "Automated Payroll",
    description: "Employees send crypto, you receive fiat. No manual conversion or spreadsheets.",
  },
  {
    title: "Merchant Settlements",
    description: "Accept crypto from customers, get fiat in your business account automatically.",
  },
]

const faqItems: FAQItem[] = [
  {
    id: "what-is",
    q: "What is a Liquidation Address?",
    a: "A permanent crypto address that automatically converts incoming cryptocurrency deposits to fiat (EUR or USD) and sends them to your linked bank account. Once set up, it works forever—no expiration, no manual steps.",
  },
  {
    id: "how-long",
    q: "How long does conversion take?",
    a: "Most conversions complete within 2-5 minutes after the crypto deposit is confirmed on the blockchain. Bank deposits typically arrive within 1-2 business days for SEPA/ACH, or same-day/next-day for Wire transfers depending on your bank.",
  },
  {
    id: "fees",
    q: "What are the fees?",
    a: "We charge a small conversion fee (typically 0.5-1%) on the exchange rate, plus a 2% developer fee. There are no setup fees, no monthly fees, and no hidden charges. You'll see the exact amount you'll receive before confirming any transaction.",
  },
  {
    id: "supported",
    q: "Which cryptocurrencies and networks are supported?",
    a: "We support 8 assets (USDC, USDT, PYUSD, USDB, EURC, DAI, USDP, ETH) across 9 blockchain networks. USDC is available on 8 networks, while USDT is available on Ethereum and Tron. See the full list above.",
  },
  {
    id: "bank-account",
    q: "Do I need to link a bank account?",
    a: "Yes, you need to link a bank account (External Account) to receive fiat deposits. You can link multiple bank accounts and choose which one to use for each address. Bank accounts are verified through our KYC process.",
  },
  {
    id: "security",
    q: "Is it secure?",
    a: "Yes. All addresses are generated through Bridge API with institutional-grade security. We use KYC verification, automatic compliance checks, and all transactions are monitored. Addresses are custodial and managed by Bridge—you don't need to manage private keys.",
  },
]

export default function LiquidationAddressPage() {
  useEffect(() => {
    // SEO: Update page title and meta
    document.title = "Liquidation Address - Auto-Convert Crypto to Fiat | Supabank"

    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Get a permanent crypto address that automatically converts USDC, USDT, and other cryptocurrencies to EUR or USD and deposits them in your bank account. No manual work required.')
    }

    // Structured Data (JSON-LD)
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "Liquidation Address",
      "description": "Permanent crypto address that automatically converts incoming cryptocurrency to fiat and deposits it in your bank account",
      "brand": {
        "@type": "Brand",
        "name": "Supabank"
      },
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "150"
      }
    }

    const howToData = {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "How to use Liquidation Address",
      "description": "Step-by-step guide to using Supabank Liquidation Address",
      "step": [
        {
          "@type": "HowToStep",
          "name": "Create Address",
          "text": "Get your dedicated crypto address in one click. The address is permanent and works forever.",
          "position": 1
        },
        {
          "@type": "HowToStep",
          "name": "Send Crypto",
          "text": "Send USDC, USDT, or other supported assets to your address on any supported blockchain network.",
          "position": 2
        },
        {
          "@type": "HowToStep",
          "name": "Automatic Conversion",
          "text": "We detect the deposit and automatically convert it to EUR or USD at market rates.",
          "position": 3
        },
        {
          "@type": "HowToStep",
          "name": "Bank Deposit",
          "text": "Funds arrive in your linked bank account via SEPA, ACH, or Wire—usually within minutes.",
          "position": 4
        }
      ]
    }

    const faqData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqItems.map(item => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.a
        }
      }))
    }

    // Remove existing structured data scripts
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]')
    existingScripts.forEach(script => {
      try {
        const data = JSON.parse(script.textContent || '{}')
        if (data['@type'] === 'Product' || data['@type'] === 'HowTo' || data['@type'] === 'FAQPage') {
          script.remove()
        }
      } catch (e) {
        // Ignore parse errors
      }
    })

    // Add new structured data
    const script1 = document.createElement('script')
    script1.type = 'application/ld+json'
    script1.textContent = JSON.stringify(structuredData)
    document.head.appendChild(script1)

    const script2 = document.createElement('script')
    script2.type = 'application/ld+json'
    script2.textContent = JSON.stringify(howToData)
    document.head.appendChild(script2)

    const script3 = document.createElement('script')
    script3.type = 'application/ld+json'
    script3.textContent = JSON.stringify(faqData)
    document.head.appendChild(script3)

    return () => {
      script1.remove()
      script2.remove()
      script3.remove()
    }
  }, [])

  return (
    <Page data-ux-contract="canon" role="main" className="bg-background text-foreground">
      {/* Unified PublicHeader component */}
      <PublicHeader />

      <main className="space-y-0" id="main">
        {/* Hero Section */}
        <section className={cn(
          "relative overflow-hidden",
          "py-12 sm:py-16 md:py-20 lg:py-24",
          // CANON: Hero gradient background (lavenderGlow for fintech feel)
          semanticTokens.heroGradient.lavenderGlow.tailwind,
          // Dark mode variant
          "dark:bg-black dark:bg-[radial-gradient(ellipse_at_20%_50%,hsla(270,95%,75%,0.08)_0%,transparent_50%),radial-gradient(ellipse_at_80%_80%,hsla(217,91%,60%,0.06)_0%,transparent_50%),hsl(var(--background))]"
        )}>
          {/* Living Brand: Breathing Gradient (syncs with LED heartbeat) - DISABLED on dark theme */}
          <BreathingGradient
            variant="lavender"
            syncWithLed
            className="absolute inset-0 z-[1] pointer-events-none opacity-30 dark:hidden"
          />
          <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6">
            <div className="space-y-6 sm:space-y-8 text-center">
              <div className={cn("inline-flex items-center justify-center rounded-full border px-4 py-2 text-sm font-medium", semanticTokens.border.default, semanticTokens.fg.muted, semanticTokens.radius.full)}>
                <CreditCard className="mr-2 h-4 w-4" />
                Product
              </div>
              <h1 className={cn("font-display text-display-sm sm:text-display-md md:text-display-lg lg:text-display-lg", semanticTokens.fg.default)}>
                Auto-Convert Crypto to Fiat
              </h1>
              <p className={cn("mx-auto max-w-3xl text-lg sm:text-xl leading-relaxed", semanticTokens.fg.muted)}>
                Get a permanent crypto address. Send USDC, USDT, or other supported assets. We automatically convert them to EUR or USD and deposit them in your bank. No manual work.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <Button asChild size="lg" className="min-h-[44px] touch-manipulation">
                  <Link to="/waitlist">
                    Get Your Address
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="min-h-[44px] touch-manipulation">
                  <Link to="/how-payments-work">
                    Learn How It Works
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Flow Diagram */}
        <section className={cn(
          "relative overflow-hidden py-12 sm:py-16 md:py-20",
          semanticTokens.harmony.calm.bg
        )}>
          {/* Color Lab: Subtle Blue-Lavender gradient */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `
                radial-gradient(ellipse at 10% 90%, hsla(217, 91%, 60%, 0.10) 0%, transparent 45%),
                radial-gradient(ellipse at 90% 10%, hsla(270, 95%, 75%, 0.08) 0%, transparent 45%)
              `
            }}
          />
          <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6">
            <div className="space-y-8 sm:space-y-10">
              <div className="text-center space-y-3">
                <Heading level={2} className={cn("sm:text-display-sm md:text-display-md", semanticTokens.fg.default)}>
                  How It Works
                </Heading>
                <p className={cn("mx-auto max-w-2xl text-base sm:text-lg", semanticTokens.fg.muted)}>
                  Four simple steps. Fully automated. Zero manual work.
                </p>
              </div>
              <ProductFlowDiagram steps={flowSteps} direction="horizontal" />
            </div>
          </div>
        </section>

        {/* Supported Assets & Chains */}
        <section className={cn("py-12 sm:py-16 md:py-20", semanticTokens.bg.surface)}>
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="space-y-8 sm:space-y-10">
              <div className="text-center space-y-3">
                <Heading level={2} className={cn("sm:text-display-sm md:text-display-md", semanticTokens.fg.default)}>
                  Supported Assets & Networks
                </Heading>
                <p className={cn("mx-auto max-w-2xl text-base sm:text-lg", semanticTokens.fg.muted)}>
                  Send from any supported cryptocurrency on any supported blockchain network.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {supportedAssets.map((asset) => (
                  <SpotlightCard
                    key={asset.name}
                    className={cn(
                      "p-5 sm:p-6 border",
                      semanticTokens.bg.surface,
                      semanticTokens.border.default
                    )}
                  >
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className={cn("text-lg font-bold", semanticTokens.fg.default)}>
                          {asset.name}
                        </h3>
                        <Badge variant="outline" className="text-xs">
                          {asset.networks} {asset.networks === 1 ? 'network' : 'networks'}
                        </Badge>
                      </div>
                      <p className={cn("text-sm leading-relaxed", semanticTokens.fg.muted)}>
                        {asset.description}
                      </p>
                    </div>
                  </SpotlightCard>
                ))}
              </div>

              <div className="mt-8 sm:mt-10">
                <h3 className={cn("text-lg sm:text-xl font-semibold mb-4 text-center", semanticTokens.fg.default)}>
                  Supported Blockchain Networks
                </h3>
                <div className="flex flex-wrap gap-2 justify-center">
                  {supportedChains.map((chain) => (
                    <Badge
                      key={chain}
                      variant="secondary"
                      className="px-3 py-1.5 text-sm"
                    >
                      {chain}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className={cn(
          "relative overflow-hidden py-12 sm:py-16 md:py-20",
          semanticTokens.harmony.calm.bg
        )}>
          {/* Color Lab: Subtle Blue-Lavender gradient */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `
                radial-gradient(ellipse at 10% 90%, hsla(217, 91%, 60%, 0.10) 0%, transparent 45%),
                radial-gradient(ellipse at 90% 10%, hsla(270, 95%, 75%, 0.08) 0%, transparent 45%)
              `
            }}
          />
          <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6">
            <div className="space-y-8 sm:space-y-10">
              <div className="text-center space-y-3">
                <Heading level={2} className={cn("sm:text-display-sm md:text-display-md", semanticTokens.fg.default)}>
                  Use Cases
                </Heading>
                <p className={cn("mx-auto max-w-2xl text-base sm:text-lg", semanticTokens.fg.muted)}>
                  Perfect for businesses and individuals who receive crypto payments regularly.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
                {useCases.map((useCase) => (
                  <SpotlightCard
                    key={useCase.title}
                    className={cn(
                      "p-6 sm:p-8 border",
                      semanticTokens.bg.surface,
                      semanticTokens.border.default
                    )}
                  >
                    <div className="space-y-3">
                      <h3 className={cn("text-lg font-bold", semanticTokens.fg.default)}>
                        {useCase.title}
                      </h3>
                      <p className={cn("text-sm leading-relaxed", semanticTokens.fg.muted)}>
                        {useCase.description}
                      </p>
                    </div>
                  </SpotlightCard>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Security & Compliance */}
        <section className={cn("py-12 sm:py-16 md:py-20", semanticTokens.bg.surface)}>
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="space-y-6 sm:space-y-8">
              <div className="text-center space-y-3">
                <Heading level={2} className={cn("sm:text-display-sm md:text-display-md", semanticTokens.fg.default)}>
                  Security & Compliance
                </Heading>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
                <SpotlightCard className={cn("p-6 sm:p-8 border", semanticTokens.bg.surface, semanticTokens.border.default)}>
                  <div className="flex items-start gap-4">
                    <div className={cn("rounded-lg p-3", semanticTokens.bg.muted)}>
                      <Shield className={cn("h-6 w-6", semanticTokens.fg.default)} />
                    </div>
                    <div className="space-y-2 flex-1">
                      <h3 className={cn("text-lg font-bold", semanticTokens.fg.default)}>
                        Institutional Security
                      </h3>
                      <p className={cn("text-sm leading-relaxed", semanticTokens.fg.muted)}>
                        All addresses are generated through Bridge API with institutional-grade security. Your private keys are never stored—addresses are custodial and managed by Bridge.
                      </p>
                    </div>
                  </div>
                </SpotlightCard>

                <SpotlightCard className={cn("p-6 sm:p-8 border", semanticTokens.bg.surface, semanticTokens.border.default)}>
                  <div className="flex items-start gap-4">
                    <div className={cn("rounded-lg p-3", semanticTokens.bg.muted)}>
                      <CheckCircle2 className={cn("h-6 w-6", semanticTokens.fg.default)} />
                    </div>
                    <div className="space-y-2 flex-1">
                      <h3 className={cn("text-lg font-bold", semanticTokens.fg.default)}>
                        Automatic Compliance
                      </h3>
                      <p className={cn("text-sm leading-relaxed", semanticTokens.fg.muted)}>
                        KYC verification, automatic compliance checks, and transaction monitoring. All transactions are screened before funds move.
                      </p>
                    </div>
                  </div>
                </SpotlightCard>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className={cn(
          "relative overflow-hidden py-12 sm:py-16 md:py-20",
          semanticTokens.harmony.calm.bg
        )}>
          {/* Color Lab: Subtle Lavender gradient */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `
                radial-gradient(ellipse at 80% 20%, hsla(270, 95%, 75%, 0.08) 0%, transparent 45%),
                radial-gradient(ellipse at 20% 80%, hsla(217, 91%, 60%, 0.06) 0%, transparent 45%)
              `
            }}
          />
          <div className="relative z-10 mx-auto max-w-4xl px-4 sm:px-6">
            <div className="space-y-8 sm:space-y-10">
              <div className="text-center space-y-3">
                <Heading level={2} className={cn("sm:text-display-sm md:text-display-md", semanticTokens.fg.default)}>
                  Frequently Asked Questions
                </Heading>
              </div>
              <FAQAccordion items={faqItems} />
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className={cn("py-12 sm:py-16 md:py-20", semanticTokens.bg.surface)}>
          <div className="mx-auto max-w-4xl px-4 sm:px-6 text-center space-y-6">
            <Heading level={2} className={cn("sm:text-display-sm md:text-display-md", semanticTokens.fg.default)}>
              Ready to Get Started?
            </Heading>
            <p className={cn("text-base sm:text-lg", semanticTokens.fg.muted)}>
              Get your permanent crypto address and start receiving automatic fiat deposits today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="min-h-[44px] touch-manipulation">
                <Link to="/waitlist">
                  Get Your Address
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="min-h-[44px] touch-manipulation">
                <Link to="/products">
                  View All Products
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <LandingFooter />
    </Page>
  )
}

